/**
 * Iter 253u · BotCheck — a lightweight, no-3rd-party bot filter for public
 * forms (helpdesk client portal signup/login/magic-link, widget ticket
 * create). Fetches a math challenge from the server, tracks the honeypot
 * + elapsed time, and exposes the answer via a controlled `value` +
 * `onChange({ challenge_token, challenge_answer, honeypot, elapsed_ms })`.
 *
 * Usage:
 *
 *   const [bot, setBot] = useState(null);
 *   ...
 *   <BotCheck value={bot} onChange={setBot} />
 *   ...
 *   await axios.post(url, { ...normalBody, bot_check: bot });
 *
 * Callers should DISABLE their submit button while `!bot?.ready`.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function BotCheck({ value, onChange, testidPrefix = "bot-check" }) {
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const issuedAtRef = useRef(null);

  const fetchChallenge = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/public/bot-check/challenge`);
      setChallenge(r.data);
      setAnswer("");
      issuedAtRef.current = Date.now();
    } catch (e) {
      setError("Couldn't load the security check. Retry?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChallenge(); /* eslint-disable-next-line */ }, []);

  // Re-emit the value whenever any field changes.
  useEffect(() => {
    if (!challenge) { onChange?.(null); return; }
    const ready = String(answer).trim() !== "" && !isNaN(Number(answer));
    onChange?.({
      challenge_token: challenge.challenge_token,
      challenge_answer: ready ? Number(answer) : 0,
      honeypot,
      elapsed_ms: issuedAtRef.current ? (Date.now() - issuedAtRef.current) : 0,
      ready,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge, answer, honeypot]);

  return (
    <div className="border border-border p-2 bg-muted/30 text-xs space-y-1.5"
         data-testid={testidPrefix}>
      {/* Honeypot — CSS-hidden from humans, autofilled by dumb bots. */}
      <label className="hidden absolute left-[-9999px]" aria-hidden="true">
        <span>Leave this blank</span>
        <input type="text" tabIndex={-1} autoComplete="off"
               value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
               data-testid={`${testidPrefix}-honeypot`} />
      </label>

      {loading ? (
        <div className="text-muted-foreground">Loading security check…</div>
      ) : error ? (
        <button type="button" onClick={fetchChallenge}
                className="text-rose-600 underline"
                data-testid={`${testidPrefix}-retry`}>{error}</button>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{challenge?.question}</span>
          <input type="number" value={answer}
                 onChange={(e) => setAnswer(e.target.value)}
                 className="h-7 w-16 border border-border bg-background px-1.5 text-xs rounded-none"
                 data-testid={`${testidPrefix}-answer`}
                 placeholder="?" required aria-label="Answer" />
          <button type="button" onClick={fetchChallenge}
                  className="text-muted-foreground hover:text-foreground text-[10px] underline"
                  data-testid={`${testidPrefix}-refresh`}>refresh</button>
        </div>
      )}
    </div>
  );
}
